import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { VISION_MISSION } from "@/data/visionMission";

// Turn plain URLs into <a> tags (kept local so this page has no dependencies on other pages).
const URL_RE = /(https?:\/\/[^\s)]+?|www\.[^\s)]+?)([.,;:!?)]*)(?=\s|$)/g;
const linkify = (text) => {
  if (!text) return text;
  const nodes = [];
  let lastIdx = 0;
  let key = 0;
  let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > lastIdx) nodes.push(text.slice(lastIdx, m.index));
    const url = m[1];
    const trailing = m[2] || "";
    const href = url.startsWith("http") ? url : `https://${url}`;
    nodes.push(
      <a
        key={key++}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2"
        style={{ color: "var(--wofy-primary)" }}
      >
        {url}
      </a>,
    );
    if (trailing) nodes.push(trailing);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) nodes.push(text.slice(lastIdx));
  return nodes;
};

const BulletList = ({ items, tone = "primary" }) => (
  <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
    {items.map((li, i) => (
      <li key={`${li}-${i}`} className="flex items-start gap-3 text-base leading-relaxed">
        <span
          className="mt-2 w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: tone === "primary" ? "var(--wofy-primary)" : "var(--wofy-text-soft)" }}
        />
        <span>{li}</span>
      </li>
    ))}
  </ul>
);

const Group = ({ g }) => (
  <div className="mt-6">
    <div className="font-display text-lg" style={{ color: "var(--wofy-text)" }}>{g.heading}</div>
    {g.body?.map((p, i) => (
      <p key={`${g.heading}-p-${i}`} className="text-base leading-relaxed mt-2">{linkify(p)}</p>
    ))}
    {g.list?.length > 0 && <BulletList items={g.list} />}
  </div>
);

export default function VisionMission() {
  const a = VISION_MISSION;

  return (
    <div data-testid="vision-mission-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-16">
      <Link
        to="/"
        data-testid="back-to-home"
        className="inline-flex items-center gap-2 text-sm mb-8"
        style={{ color: "var(--wofy-text-soft)" }}
      >
        <ArrowLeft className="w-4 h-4" /> Back home
      </Link>

      {/* HERO */}
      <header className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
        <div className="lg:col-span-7">
          <div className="overline">{a.overline}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-4">
            Social Pulse{" "}
            <span className="italic" style={{ color: "var(--wofy-primary)" }}>&mdash;</span>{" "}
            <span style={{ color: "var(--wofy-text-soft)" }}>{a.title}</span>
          </h1>
          <div className="mt-6">
            <Link
              to="/why-we-build"
              data-testid="link-why-we-build"
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium hover:bg-black/[0.02] transition-colors"
              style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
            >
              Read the founding manifesto
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
        {a.hero_image && (
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
              <img src={a.hero_image} alt="Social Pulse — Vision & Mission" className="w-full h-[320px] lg:h-[380px] object-cover" />
            </div>
          </div>
        )}
      </header>

      {/* INTRO */}
      <div className="mt-16 md:mt-20 grid lg:grid-cols-12 gap-8 md:gap-12">
        <div className="lg:col-span-3">
          <div className="overline">Introduction</div>
        </div>
        <div className="lg:col-span-9 space-y-5">
          <p className="text-lg leading-relaxed font-display" style={{ color: "var(--wofy-text)" }}>
            {linkify(a.intro)}
          </p>
          <p className="text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
            {linkify(a.lead)}
          </p>
        </div>
      </div>

      {/* BODY with sticky TOC */}
      <div className="mt-16 md:mt-20 grid lg:grid-cols-12 gap-8 md:gap-12">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-24">
            <div className="overline mb-3">Contents</div>
            <nav className="flex flex-col gap-1.5">
              {a.sections.map((s) => (
                <a
                  key={s.slug}
                  href={`#${s.slug}`}
                  data-testid={`toc-${s.slug}`}
                  className="text-sm leading-snug hover:underline underline-offset-4"
                  style={{ color: "var(--wofy-text-soft)" }}
                >
                  {s.heading}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <div className="lg:col-span-9 space-y-14 md:space-y-16">
          {a.sections.map((sec) => (
            <section key={sec.slug} id={sec.slug} className="scroll-mt-24">
              <h2 className="font-display text-2xl md:text-3xl leading-tight">{sec.heading}</h2>
              <div className="mt-5 space-y-4">
                {sec.body?.map((p, i) => (
                  <p key={`${sec.slug}-body-${i}`} className="text-base leading-relaxed" style={{ color: "var(--wofy-text)" }}>
                    {linkify(p)}
                  </p>
                ))}
                {sec.list?.length > 0 && <BulletList items={sec.list} />}
                {sec.outro && (
                  <p className="text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
                    {linkify(sec.outro)}
                  </p>
                )}
                {sec.list_after?.length > 0 && <BulletList items={sec.list_after} />}
                {sec.groups?.map((g) => <Group key={`${sec.slug}-${g.heading}`} g={g} />)}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* CLOSING CTA */}
      <aside
        className="mt-20 rounded-3xl p-8 md:p-12 relative overflow-hidden"
        style={{ background: "var(--wofy-primary)", color: "#FDFBF7" }}
      >
        <div className="overline text-white/70">JOIN THE MOVEMENT</div>
        <h3 className="font-display text-3xl md:text-4xl mt-3 max-w-2xl leading-tight">
          One billion acts of kindness. Starting with one.
        </h3>
        <p className="mt-4 max-w-2xl text-white/85">
          Give time, share a skill, mentor a student, sponsor a family, or simply check on a neighbor.
          Every verified act strengthens communities and creates a more compassionate world.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/register" className="rounded-full px-6 py-3 text-sm font-medium" style={{ background: "#FDFBF7", color: "var(--wofy-text)" }}>
            Create account
          </Link>
          <Link to="/offer-help" className="rounded-full px-6 py-3 text-sm font-medium border border-white/40 text-white hover:bg-white/10 transition-colors">
            Offer help today
          </Link>
        </div>
      </aside>
    </div>
  );
}
